export const landingPageContent = {
    en: {
      nav: {
        home: "Home",
        products: "Products",
        howItWorks: "How It Works",
        infrastructure: "Infrastructure",
        about: "About",
        partners: "Partners & Customers"
      },
      hero: {
        badge: "Complete AI Platform",
        title: "Build, Deploy, and Scale AI Agents On-Premise",
        subtitle: "Intelligent automation and voice AI powered by our cloud OS. Enterprise-grade with complete control.",
        primaryCta: "Schedule Demo",
        secondaryCta: "Solution Brief",
        solutionBriefUrl: "https://www.supermicro.com/solutions/Solution_Brief_SMCI_AMD_Piovation.pdf",
        features: [
          "PioAgent - Automation",
          "PioVoice - Voice AI",
          "PioSphere - Cloud OS"
        ]
      },
      products: {
        title: "Three Products, One Platform",
        subtitle: "Build and deploy AI at scale",
        pioAgent: {
          name: "PioAgent",
          tagline: "Intelligent Automation",
          description: "Orchestrate AI agents that automate workflows and execute multi-step tasks.",
          features: [
            "Multi-agent orchestration",
            "RAG-powered knowledge",
            "API integration",
            "200+ model support"
          ],
          icon: "brainCircuit",
          cta: "Learn More"
        },
        pioVoice: {
          name: "PioVoice",
          tagline: "Voice AI Platform",
          description: "Production-ready voice agents with ultra-low latency and natural conversations.",
          features: [
            "Sub-500ms response time",
            "Human-like voices",
            "Live call integration",
            "SIP & DTMF support"
          ],
          icon: "messageCircle",
          cta: "Learn More"
        },
        pioSphere: {
          name: "PioSphere",
          tagline: "Cloud Operating System",
          description: "Deploy on any infrastructure with complete control, from edge to cloud.",
          features: [
            "Any GPU vendor",
            "Multi-tenant ready",
            "Deploy in minutes",
            "100% data sovereignty"
          ],
          icon: "cloud",
          cta: "Learn More"
        }
      },
      howItWorks: {
        title: "How It Works",
        subtitle: "Idea to production in minutes",
        steps: [
          {
            number: "01",
            title: "Choose Product",
            description: "PioAgent for automation or PioVoice for voice AI",
            icon: "layers"
          },
          {
            number: "02",
            title: "Configure",
            description: "Connect models, tools, and data sources",
            icon: "settings"
          },
          {
            number: "03",
            title: "Deploy",
            description: "Launch on our cloud or your own hardware",
            icon: "rocket"
          },
          {
            number: "04",
            title: "Scale",
            description: "Grow to production with monitoring",
            icon: "barChart2"
          }
        ]
      },
      // videoSection: {
      //   title: "See PioSphere in Action",
      //   subtitle: "Watch how organizations transform their operations with AI",
      //   placeholder: "Video coming soon"
      // },
      infrastructure: {
        title: "Powered by PioSphere",
        subtitle: "Enterprise cloud OS with complete control",
        description: "Deploy on any GPU. Scale from edge to cloud. Sub-500ms latency. SOC2 compliant.",
        features: [
          {
            title: "Enterprise Automation",
            description: "Automate workflows across departments",
            icon: "zap",
            badge: "PioAgent"
          },
          {
            title: "Knowledge Management",
            description: "RAG-powered document search",
            icon: "database",
            badge: "PioAgent"
          },
          {
            title: "Research & Development",
            description: "AI agents for data analysis",
            icon: "search",
            badge: "PioAgent"
          },
          {
            title: "Customer Support",
            description: "24/7 voice conversations",
            icon: "messageCircle",
            badge: "PioVoice"
          },
          {
            title: "Sales & Outreach",
            description: "Automated calls with transfers",
            icon: "users",
            badge: "PioVoice"
          },
          {
            title: "Appointment Scheduling",
            description: "Voice booking and reminders",
            icon: "clock",
            badge: "PioVoice"
          }
        ],
        useCases: [
          {
            title: "Migrate to On-Prem",
            description: "Move from hyperscaler while keeping endpoints",
            icon: "server"
          },
          {
            title: "Evolve Cloud Business",
            description: "Sell tokens and apps, not virtual GPUs",
            icon: "network"
          },
          {
            title: "Build AI-Ready Cloud",
            description: "Complete stack from scratch",
            icon: "settings"
          }
        ],
        cta: {
          title: "Deploy on your infrastructure?",
          button: "Schedule Demo"
        }
      },
      about: {
        title: "About Piovation",
        subtitle: "AI Innovation in Europe",
        founderTitle: "Founder & CEO",
        story: "Founded by Mazda Sabony in Munich. We democratize AI technology with data sovereignty and compliance. Enterprise-grade solutions for organizations of any size.",
        team: {
          title: "Leadership",
          members: [
            {
              name: "Mazda Sabony",
              position: "Co-Founder & CEO",
              description: "25+ years HPC & AI",
              imagePath: "/images/team/sabony.jpg"
            },
            {
              name: "A. Kramer",
              position: "Co-Founder & CTO",
              description: "15+ years GPU optimization",
              imagePath: "/images/team/kamer.jpg"
            },
            {
              name: "Dr. Walter Berger",
              position: "Strategic Advisor",
              description: "30+ years M&A & Strategy",
              imagePath: "/images/team/berger.jpg"
            },
            {
              name: "Dr. Rene Meyer",
              position: "Technical Advisor",
              description: "25+ years HPC research",
              imagePath: "/images/team/meyer.jpg"
            }
          ]
        }
      },
      partners: {
        title: "Partners & Customers",
        subtitle: "Trusted by industry leaders",
        description: "Leading technology providers and public sector organizations.",
        partners: [
          {
            name: "AMD",
            category: "GPU & Processor Technology",
            imagePath: "/images/partners/AMD_Logo.png",
            websiteUrl: "https://www.amd.com"
          },
          {
            name: "Supermicro",
            category: "Enterprise Server Infrastructure",
            imagePath: "/images/partners/smc_logo.png",
            websiteUrl: "https://www.supermicro.com"
          },
          {
            name: "GigaIO",
            category: "Infrastructure Technology",
            imagePath: "/images/partners/gigaio.png",
            websiteUrl: "https://www.gigaio.com"
          },
          {
            name: "AMAX",
            category: "High-Performance Computing",
            imagePath: "/images/partners/amax.jpg",
            websiteUrl: "https://www.amax.com"
          },
          {
            name: "Boston-IT",
            category: "IT Systems Integrator",
            imagePath: "/images/partners/boston-it.png",
            websiteUrl: "https://www.boston-it.de"
          },
          {
            name: "Regio Tourismus GmbH Augsburg",
            category: "Public Sector Client",
            imagePath: "/images/partners/regio-ausgburg.jpg",
            websiteUrl: "https://www.augsburg-tourismus.de"
          },
          {
            name: "Stadt Diepholz",
            category: "Municipal Government",
            imagePath: "/images/partners/stadt-diepholz.jpeg",
            websiteUrl: "https://www.diepholz.de"
          },
          {
            name: "Gemeinde Grasbrunn",
            category: "Local Government",
            imagePath: "/images/partners/gemeinde-grasbrunn.jpeg",
            websiteUrl: "https://www.grasbrunn.de"
          },
          {
            name: "doitnow",
            category: "Technology Partner",
            imagePath: "https://www.doitnowgroup.com/wp-content/uploads/2024/06/logo-noir.svg",
            websiteUrl: "https://www.doitnowgroup.com"
          },
          {
            name: "parcs IT-Consulting",
            category: "IT Consulting",
            imagePath: "/images/partners/parcs.png",
            websiteUrl: "https://www.parcs.de"
          }
        ]
      },
      cta: {
        title: "Ready to Start?",
        subtitle: "Deploy AI on your infrastructure with complete control.",
        button: "Schedule Demo",
        secondaryButton: "Solution Brief",
        solutionBriefUrl: "https://www.supermicro.com/solutions/Solution_Brief_SMCI_AMD_Piovation.pdf"
      },
      auth: {
        login: "Login",
        signup: "Get Started"
      },
      footer: {
        company: {
          title: "Company",
          links: [
            "About Us",
            "Solution Brief",
            "Demo"
          ]
        },
        legal: {
          title: "Legal",
          links: [
            "Impressum",
            "Privacy Policy"
          ]
        },
        contact: {
          title: "Contact",
          address: "Maximilianstrasse 35a, 80539 München",
          email: "info@piovation.com",
          social: {
            linkedin: "https://www.linkedin.com/company/piovation",
            twitter: "https://twitter.com/piovation",
            github: "https://github.com/piovation"
          }
        }
      }
    },
    de: {
      nav: {
        home: "Startseite",
        products: "Produkte",
        howItWorks: "So funktioniert's",
        infrastructure: "Infrastruktur",
        about: "Über uns",
        partners: "Partner & Kunden"
      },
      hero: {
        badge: "Komplette KI-Plattform",
        title: "KI-Agenten On-Premise Bauen, Bereitstellen und Skalieren",
        subtitle: "Automatisierung und Sprach-KI auf unserem Cloud-OS. Enterprise mit voller Kontrolle.",
        primaryCta: "Demo vereinbaren",
        secondaryCta: "Solution Brief",
        solutionBriefUrl: "https://www.supermicro.com/solutions/Solution_Brief_SMCI_AMD_Piovation.pdf",
        features: [
          "PioAgent - Automatisierung",
          "PioVoice - Sprach-KI",
          "PioSphere - Cloud OS"
        ]
      },
      products: {
        title: "Drei Produkte, Eine Plattform",
        subtitle: "KI im großen Maßstab",
        pioAgent: {
          name: "PioAgent",
          tagline: "Intelligente Automatisierung",
          description: "KI-Agenten für Workflows und mehrstufige Aufgaben.",
          features: [
            "Multi-Agent-Orchestrierung",
            "RAG-basiertes Wissen",
            "API-Integration",
            "200+ Modelle"
          ],
          icon: "brainCircuit",
          cta: "Mehr erfahren"
        },
        pioVoice: {
          name: "PioVoice",
          tagline: "Sprach-KI-Plattform",
          description: "Produktionsreife Sprach-Agenten mit niedriger Latenz.",
          features: [
            "Unter 500ms Antwortzeit",
            "Menschenähnliche Stimmen",
            "Live-Call-Integration",
            "SIP & DTMF"
          ],
          icon: "messageCircle",
          cta: "Mehr erfahren"
        },
        pioSphere: {
          name: "PioSphere",
          tagline: "Cloud-Betriebssystem",
          description: "Bereitstellung auf jeder Infrastruktur mit voller Kontrolle.",
          features: [
            "Jeder GPU-Anbieter",
            "Multi-Tenant",
            "In Minuten bereit",
            "100% Datensouveränität"
          ],
          icon: "cloud",
          cta: "Mehr erfahren"
        }
      },      
      howItWorks: {
        title: "So Funktioniert Es",
        subtitle: "Idee zu Produktion in Minuten",
        steps: [
          {
            number: "01",
            title: "Produkt wählen",
            description: "PioAgent für Automatisierung oder PioVoice für Sprach-KI",
            icon: "layers"
          },
          {
            number: "02",
            title: "Konfigurieren",
            description: "Modelle, Tools und Daten verbinden",
            icon: "settings"
          },
          {
            number: "03",
            title: "Bereitstellen",
            description: "Auf unserer Cloud oder Ihrer Hardware",
            icon: "rocket"
          },
          {
            number: "04",
            title: "Skalieren",
            description: "Zur Produktion mit Monitoring",
            icon: "barChart2"
          }
        ]
      },
      // videoSection: {
      //   title: "PioSphere in Aktion erleben",
      //   subtitle: "Sehen Sie, wie Organisationen ihre Abläufe mit KI transformieren",
      //   placeholder: "Video kommt bald"
      // },
      infrastructure: {
        title: "Angetrieben durch PioSphere",
        subtitle: "Enterprise Cloud-OS mit voller Kontrolle",
        description: "Jede GPU. Edge bis Cloud. Unter 500ms. SOC2-konform.",
        features: [
          {
            title: "Unternehmens-Automatisierung",
            description: "Workflows über Abteilungen hinweg",
            icon: "zap",
            badge: "PioAgent"
          },
          {
            title: "Wissensmanagement",
            description: "RAG-basierte Dokumentensuche",
            icon: "database",
            badge: "PioAgent"
          },
          {
            title: "Forschung & Entwicklung",
            description: "KI-Agenten für Datenanalyse",
            icon: "search",
            badge: "PioAgent"
          },
          {
            title: "Kundensupport",
            description: "24/7 Sprachgespräche",
            icon: "messageCircle",
            badge: "PioVoice"
          },
          {
            title: "Vertrieb & Outreach",
            description: "Automatische Anrufe mit Transfers",
            icon: "users",
            badge: "PioVoice"
          },
          {
            title: "Termin-Planung",
            description: "Sprach-Buchung und Erinnerungen",
            icon: "clock",
            badge: "PioVoice"
          }
        ],
        useCases: [
          {
            title: "Migration zu On-Prem",
            description: "Von Hyperscaler mit gleichen Endpoints",
            icon: "server"
          },
          {
            title: "Cloud-Geschäft Entwickeln",
            description: "Tokens und Apps, nicht virtuelle GPUs",
            icon: "network"
          },
          {
            title: "KI-Cloud Aufbauen",
            description: "Kompletter Stack von Grund auf",
            icon: "settings"
          }
        ],
        cta: {
          title: "Auf Ihrer Infrastruktur bereitstellen?",
          button: "Demo vereinbaren"
        }
      },
      about: {
        title: "Über Piovation",
        subtitle: "KI-Innovation in Europa",
        founderTitle: "Gründer & CEO",
        story: "Gegründet von Mazda Sabony in München. KI-Demokratisierung mit Datensouveränität. Enterprise-Lösungen für jede Organisation.",
        team: {
          title: "Leadership",
          members: [
            {
              name: "Mazda Sabony",
              position: "Co-Founder & CEO",
              description: "25+ Jahre HPC & KI",
              imagePath: "/images/team/sabony.jpg"
            },
            {
              name: "A. Kramer",
              position: "Co-Founder & CTO",
              description: "15+ Jahre GPU-Optimierung",
              imagePath: "/images/team/kamer.jpg"
            },
            {
              name: "Dr. Walter Berger",
              position: "Strategic Advisor",
              description: "30+ Jahre M&A & Strategie",
              imagePath: "/images/team/berger.jpg"
            },
            {
              name: "Dr. Rene Meyer",
              position: "Technical Advisor",
              description: "25+ Jahre HPC-Forschung",
              imagePath: "/images/team/meyer.jpg"
            }
          ]
        }
      },
      partners: {
        title: "Partner & Kunden",
        subtitle: "Vertrauen von Branchenführern",
        description: "Führende Technologieanbieter und öffentliche Organisationen.",
        partners: [
          {
            name: "AMD",
            category: "GPU & Prozessor-Technologie",
            imagePath: "/images/partners/AMD_Logo.png",
            websiteUrl: "https://www.amd.com"
          },
          {
            name: "Supermicro",
            category: "Enterprise Server Infrastruktur",
            imagePath: "/images/partners/smc_logo.png",
            websiteUrl: "https://www.supermicro.com"
          },
          {
            name: "GigaIO",
            category: "Infrastrukturtechnologie",
            imagePath: "/images/partners/gigaio.png",
            websiteUrl: "https://www.gigaio.com"
          },
          {
            name: "AMAX",
            category: "High-Performance Computing",
            imagePath: "/images/partners/amax.jpg",
            websiteUrl: "https://www.amax.com"
          },
          {
            name: "Boston-IT",
            category: "IT-Lösungen",
            imagePath: "/images/partners/boston-it.png",
            websiteUrl: "https://www.boston-it.de"
          },
          {
            name: "Regio Tourismus GmbH Augsburg",
            category: "Öffentlicher Sektor Kunde",
            imagePath: "/images/partners/regio-ausgburg.jpg",
            websiteUrl: "https://www.augsburg-tourismus.de"
          },
          {
            name: "Stadt Diepholz",
            category: "Stadtverwaltung",
            imagePath: "/images/partners/stadt-diepholz.jpeg",
            websiteUrl: "https://www.diepholz.de"
          },
          {
            name: "Gemeinde Grasbrunn",
            category: "Gemeindeverwaltung",
            imagePath: "/images/partners/gemeinde-grasbrunn.jpeg",
            websiteUrl: "https://www.grasbrunn.de"
          },
          {
            name: "doitnow",
            category: "Technologiepartner",
            imagePath: "https://www.doitnowgroup.com/wp-content/uploads/2024/06/logo-noir.svg",
            websiteUrl: "https://www.doitnowgroup.com"
          },
          {
            name: "parcs IT-Consulting",
            category: "IT-Beratung",
            imagePath: "/images/partners/parcs.png",
            websiteUrl: "https://www.parcs.de"
          }
        ]
      },
      cta: {
        title: "Bereit zu Starten?",
        subtitle: "KI auf Ihrer Infrastruktur mit voller Kontrolle.",
        button: "Demo vereinbaren",
        secondaryButton: "Solution Brief",
        solutionBriefUrl: "https://www.supermicro.com/solutions/Solution_Brief_SMCI_AMD_Piovation.pdf"
      },
      auth: {
        login: "Anmelden",
        signup: "Jetzt starten"
      },
      footer: {
        company: {
          title: "Unternehmen",
          links: [
            "Über uns",
            "Solution Brief",
            "Demo"
          ]
        },
        legal: {
          title: "Rechtliches",
          links: [
            "Impressum",
            "Datenschutz"
          ]
        },
        contact: {
          title: "Kontakt",
          address: "Maximilianstrasse 35a, 80539 München",
          email: "info@piovation.com",
          social: {
            linkedin: "https://www.linkedin.com/company/piovation",
            twitter: "https://twitter.com/piovation",
            github: "https://github.com/piovation"
          }
        }
      }
    }
  }; 